import { cn } from "@/lib/styles.utils";
import { useState, type FC } from "react";
import { Icon } from "../icons/Icon";

interface CheckboxProps {
  title: string;
  description?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  onChange?: (new_value: boolean) => void;
  id: string;
}

const InputCheckbox: FC<CheckboxProps> = ({
  title,
  description,
  checked,
  defaultChecked,
  disabled,
  onChange,
  id,
}) => {
  const [internal, setInternal] = useState<boolean>(defaultChecked ?? false);
  const isControlled = typeof checked === "boolean";
  const isChecked = isControlled ? (checked as boolean) : internal;

  return (
    <label
      htmlFor={id}
      className={cn(
        "group inline-flex gap-4 items-center cursor-pointer select-none",
        disabled ? "cursor-not-allowed opacity-100" : "",
      )}
    >
      <span
        className={cn(
          "relative grid place-items-center size-[18px] rounded-[2px] border transition-colors shrink-0",
          !disabled && !isChecked && "border-black",
          isChecked && !disabled && "border-transparent bg-[#D7192D]",
          disabled && !isChecked && "border-[#B1B1B1]",
          disabled && isChecked && "bg-[#B1B1B1] border-transparent",
          "has-[input:focus-visible]:border-[#B9191A]",
        )}
      >
        {isChecked && (
          <Icon
            name="check"
            className="pointer-events-none size-4 text-white"
          />
        )}

        <input
          id={id}
          type="checkbox"
          disabled={disabled}
          checked={isControlled ? checked : undefined}
          defaultChecked={isControlled ? undefined : defaultChecked}
          onChange={(e) => {
            if (disabled) return;
            if (!isControlled) setInternal(e.target.checked);
            onChange?.(e.target.checked);
          }}
          aria-describedby={description ? `${id}-desc` : undefined}
          className="absolute inset-0 opacity-0 appearance-none m-0 p-0"
        />
      </span>

      <span className="flex flex-col leading-none gap-0.5">
        <span
          className={cn(
            "font-ib-bold leading-6 -mt-[1px]",
            disabled ? "text-[#B1B1B1]" : "text-neutral-900",
          )}
        >
          {title}
        </span>
        {description && (
          <span
            id={`${id}-desc`}
            className={cn(disabled ? "text-[#B1B1B1]" : "text-neutral-600")}
          >
            {description}
          </span>
        )}
      </span>
    </label>
  );
};

export default InputCheckbox;
